import { router } from "@inertiajs/react"
import { useCallback, useEffect, useRef, useState } from "react"

import { getCableConsumer } from "@/lib/cable"

/**
 * OKS-960 — Brand-side live import hook.
 *
 * Subscribes to `BrandChannel` and, on each lightweight `refetch` signal, fires
 * a targeted Inertia partial reload of ONLY the props the caller names. The
 * signal carries prop names and no data (PRD D12) — the reload recomputes
 * everything server-side inside the brand scope that already enforces
 * isolation, so nothing about tenancy moves to the client.
 *
 * Mounted at PAGE level (`Brand/Settings/Products`, `Brand/Settings/Integrations`)
 * rather than in the shell, so every other brand surface stays socket-free.
 * Same posture as `useActivityLive`.
 *
 * ## Liveness follows the actor (PRD D13)
 *
 * The stream is keyed to the USER, so a viewer receives signals for their own
 * import only — and receives them in every tab they have open, which is why
 * `connected` is reported back rather than inferred from "did I just submit".
 *
 * ## Coalescing is required, not an optimisation (AC7)
 *
 * Two pages landing together must produce ONE visible update; the requirement
 * is "reflects the latest state", never "one render per page". A signal
 * arriving while a reload is in flight sets `dirty` and returns; `onFinish`
 * fires exactly one trailing reload. Without this a 20-page import would queue
 * 20 round trips whose intermediate results nobody sees.
 *
 * ## Degrades silently (PRD D11)
 *
 * If the transport is unavailable the hook is a no-op: no error, no
 * connection-status UI. The page still renders correct state on load, because
 * progress is durable server state rather than something this hook accumulates.
 */
export function useBrandSyncLive({
  only,
  enabled = true,
}: {
  only: string[]
  /**
   * ⚠️ Core Rule 12. When false the hook opens no subscription and fires no
   * reload, so a surface with an in-progress edit cannot have it swept away by
   * a signal it has no use for. The Integrations page sets this false while
   * the setup wizard is showing — an import can outlive its store, and a
   * `shopify` reload arriving mid-typing would re-render the wizard from
   * scratch.
   *
   * `connected` stays false while disabled, which is the honest answer: no
   * socket means nothing is arriving, and the snapshot rendering is correct.
   */
  enabled?: boolean
}) {
  /**
   * ⚠️ Reported back to the caller so the surface can tell LIVE from SNAPSHOT
   * (AC19 / Core Rule 11: "where nothing is arriving, nothing may move").
   *
   * Defaults to FALSE deliberately. An optimistic default would spin a
   * loader before the socket is up — precisely the lie Core Rule 11 names. The
   * cost is a brief snapshot→live transition on first paint, which is the safe
   * direction: claiming less liveness than you have is recoverable, claiming
   * more is the defect.
   *
   * This is NOT connection-status UI (D11 forbids that). Nothing here tells the
   * brand about a socket; it selects between two renderings of the same fact.
   */
  const [connected, setConnected] = useState(false)

  // Held in refs, not state: they must not trigger a render, and the
  // subscription closure must read the CURRENT value rather than the one
  // captured when it was created.
  const inFlight = useRef(false)
  const dirty = useRef(false)
  const onlyRef = useRef(only)
  onlyRef.current = only

  // ⚠️ A NAMED function expression, so the trailing pass can call itself
  // through its own binding rather than through the outer `const` — which
  // would be a use-before-declaration inside its own closure.
  const reload = useCallback(function runReload() {
    if (inFlight.current) {
      // A reload is already running — remember that more arrived and let
      // `onFinish` fire exactly one trailing pass.
      dirty.current = true
      return
    }

    inFlight.current = true
    router.reload({
      only: onlyRef.current,
      preserveUrl: true,
      onFinish: () => {
        inFlight.current = false
        if (dirty.current) {
          dirty.current = false
          runReload()
        }
      },
    })
  }, [])

  useEffect(() => {
    if (!enabled) return

    let consumer
    try {
      consumer = getCableConsumer()
    } catch {
      // Transport unavailable (e.g. WebSocket blocked). Real-time is an
      // enhancement, never a dependency — the surface renders the snapshot
      // variant because `connected` stays false.
      return
    }

    const subscription = consumer.subscriptions.create(
      { channel: "BrandChannel" },
      {
        connected() {
          setConnected(true)
          // Catch-up on initial connect AND every reconnect, so progress that
          // landed while the socket was down is reflected without a manual
          // reload. Same reason `useRealtimeRefetch` does it.
          reload()
        },
        disconnected() {
          setConnected(false)
        },
        received(data: { type?: string; reload?: unknown }) {
          if (data?.type !== "refetch") return
          reload()
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [reload, enabled])

  return { connected }
}
